import { useCallback, useEffect, useRef } from 'react';

/**
 * Trailing-edge debounce: each call resets the timer, so only the last call within
 * the window fires. A call still pending on unmount fires then: navigating away right
 * after a burst must not lose the work it stands for (other screens' caches to refresh).
 */
export function useDebouncedCallback(callback: () => void, delayMs: number): () => void {
  const callbackRef = useRef(callback);
  useEffect(() => {
    callbackRef.current = callback;
  });

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (timeoutRef.current == null) return;
    clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
    callbackRef.current();
  }, []);

  return useCallback(() => {
    if (timeoutRef.current != null) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      callbackRef.current();
    }, delayMs);
  }, [delayMs]);
}
